/**
 * Route: /contract/:token — publieke contract-portal
 *
 * Ouder tekent digitaal het plaatsingscontract. Mechaniek is 1-op-1 met
 * AanbodPage: viewer + naam-veld + voorwaarden-checkbox + accept.
 *
 * Bij success: bevestigingsscherm; auto-mail met signed PDF is server-side.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Check, ShieldCheck, FileSignature, Baby, MapPin, Calendar, Coins, FileText } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const DAGDEEL_LABEL = { hele_dag: "Hele dag", ochtend: "Ochtend", middag: "Middag", avond: "Avond" };

export default function ContractPortalPage() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [voorwaarden, setVoorwaarden] = useState(false);
  const [opmerking, setOpmerking] = useState("");
  const [busy, setBusy] = useState(false);
  const [signedAt, setSignedAt] = useState(null);

  useEffect(() => {
    api.get(`/public/contract/${token}`)
      .then(r => {
        setDoc(r.data);
        if (r.data.status === "getekend") setSignedAt(r.data.signed_at);
        setName(`${r.data.ouder_voornaam || ""} ${r.data.ouder_achternaam || ""}`.trim());
      })
      .catch(e => setError(formatApiError(e.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Vul je naam in"); return; }
    if (!voorwaarden) { toast.error("Ga akkoord met de contractvoorwaarden"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/public/contract/${token}/decide`, {
        accept: true,
        ondertekenaar_naam: name.trim(),
        accept_voorwaarden: true,
        opmerking: opmerking || undefined,
      });
      setSignedAt(r.data.signed_at);
      toast.success("Contract getekend — check je mail voor het bewijsstuk");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Contract laden…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <Card className="max-w-md p-6 text-center" data-testid="contract-error">
        <div className="text-red-800 font-semibold mb-1">Contract niet gevonden</div>
        <div className="text-sm text-neutral-600">{error}</div>
      </Card>
    </div>
  );

  const primary = doc.kleur_primair || "#2D4A3E";
  const isSigned = doc.status === "getekend" || signedAt;
  const kind = `${doc.kind_voornaam || ""} ${doc.kind_achternaam || ""}`.trim();

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white" data-testid="contract-portal">
      {/* Header */}
      <div className="border-b border-neutral-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          {doc.organisation_logo
            ? <img src={doc.organisation_logo} alt="" className="h-9 w-9 rounded object-contain" />
            : <div className="h-9 w-9 rounded-full" style={{ background: primary }} />}
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">Plaatsingscontract</div>
            <div className="font-semibold text-neutral-900">{doc.organisation_name}</div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Sign-status banner */}
        {isSigned ? (
          <Card className="p-5 bg-emerald-50 border-emerald-200" data-testid="signed-banner">
            <div className="flex items-start gap-3">
              <Check className="h-6 w-6 text-emerald-700 shrink-0" />
              <div>
                <div className="font-semibold text-emerald-900">Contract getekend</div>
                <div className="text-sm text-emerald-800 mt-1">
                  {signedAt && `Op ${new Date(signedAt).toLocaleString("nl-NL")}. `}
                  Het bewijsstuk is naar je e-mail verstuurd.
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="p-5 border-neutral-200" data-testid="intro-banner">
            <div className="flex items-start gap-3">
              <FileSignature className="h-6 w-6 shrink-0" style={{ color: primary }} />
              <div>
                <div className="font-semibold text-neutral-900">
                  Bekijk en teken het contract voor {kind}
                </div>
                <div className="text-sm text-neutral-600 mt-1">
                  Lees de plaatsings-details hieronder zorgvuldig. Onderaan kun je digitaal ondertekenen.
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Contract-details */}
        <Card className="p-5" data-testid="contract-details">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-neutral-900 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" style={{ color: primary }} /> Plaatsings-details
            </h2>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/public/contract/${token}/pdf`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border rounded hover:bg-neutral-50"
              style={{ color: primary, borderColor: primary + "40" }}
              data-testid="btn-download-preview-pdf"
            >
              <FileText className="h-3.5 w-3.5" />
              {isSigned ? "PDF-bewijsstuk" : "Concept-PDF lezen"}
            </a>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Detail icon={<Baby className="h-4 w-4 text-neutral-500" />}
                    label="Kind" value={`${kind}${doc.kind_geboortedatum ? ` · geb. ${doc.kind_geboortedatum}` : ""}`} />
            <Detail icon={<MapPin className="h-4 w-4 text-neutral-500" />}
                    label="Locatie"
                    value={doc.location_name + (doc.location_plaats ? ` · ${doc.location_plaats}` : "")}
                    testid="detail-location"/>
            <Detail label="Groep" value={doc.group_name || "-"} testid="detail-group"/>
            <Detail icon={<Calendar className="h-4 w-4 text-neutral-500" />}
                    label="Startdatum" value={doc.start_date || "-"} testid="detail-startdate"/>
            <Detail label="Uren/week" value={doc.uren_per_week ? `${doc.uren_per_week}u` : "-"} testid="detail-hours"/>
            <Detail icon={<Coins className="h-4 w-4 text-neutral-500" />}
                    label="Uurtarief" value={`€ ${Number(doc.uurtarief || 0).toFixed(2)}`} testid="detail-rate"/>
          </dl>
          <div className="mt-3 pt-3 border-t border-neutral-100">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Dagen</div>
            <div className="flex flex-wrap gap-1.5" data-testid="detail-dagen">
              {(doc.dagen || []).map((d, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded bg-neutral-100 border">
                  {(doc.dag_namen || [])[d.weekday] || `Dag ${d.weekday}`} — {DAGDEEL_LABEL[d.dagdeel] || d.dagdeel}
                </span>
              ))}
              {(doc.dagen || []).length === 0 && <span className="text-xs text-neutral-500">Geen dagen opgegeven</span>}
            </div>
          </div>
        </Card>

        {/* Signature form — alleen tonen als nog niet getekend */}
        {!isSigned && (
          <Card className="p-5" data-testid="sign-form">
            <h2 className="font-semibold mb-4 text-neutral-900">Digitaal ondertekenen</h2>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500">Volledige naam</Label>
                <Input value={name} onChange={e => setName(e.target.value)}
                       data-testid="sign-name-input"
                       placeholder="Piet Jansen" className="mt-1"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500">Toelichting (optioneel)</Label>
                <Textarea value={opmerking} onChange={e => setOpmerking(e.target.value)}
                          data-testid="sign-opmerking"
                          placeholder="Bijv. een vraag of bijzonderheid" className="mt-1 min-h-[64px]"/>
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox checked={voorwaarden}
                          onCheckedChange={v => setVoorwaarden(!!v)}
                          data-testid="sign-voorwaarden"/>
                <span className="text-sm text-neutral-700">
                  Ik ga akkoord met de <a href="https://www.rijksoverheid.nl/onderwerpen/kinderopvang"
                    target="_blank" rel="noopener noreferrer"
                    className="underline" style={{ color: primary }}>plaatsingsvoorwaarden</a> en verklaar
                  bevoegd te zijn dit contract namens de ouders te ondertekenen.
                </span>
              </label>
              <Button
                onClick={submit}
                disabled={busy || !name.trim() || !voorwaarden}
                className="w-full text-white mt-2"
                style={{ background: primary }}
                data-testid="sign-submit"
              >
                {busy ? "Ondertekenen…" : <><FileSignature className="h-4 w-4 mr-2" /> Digitaal ondertekenen</>}
              </Button>
              <p className="text-[11px] text-neutral-500 text-center">
                Je IP-adres en tijdstempel worden vastgelegd als tamper-evidence bewijs.
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function Detail({ icon, label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-neutral-500 mb-0.5 flex items-center gap-1">
        {icon}{label}
      </div>
      <div className="text-neutral-900">{value}</div>
    </div>
  );
}
